import React, { createContext, useContext, useMemo, useState } from 'react'
import { Timestamp } from 'google-protobuf/google/protobuf/timestamp_pb'
import { DOCUMENT_TYPE } from '@trustero/trustero-api-web/lib/attachment/attachment_pb'
import {
  SetLinkRequest,
  LinkDoc,
} from '@trustero/trustero-api-web/lib/linker/linker_pb'
import { LinkerPromiseClient } from '@trustero/trustero-api-web/lib/linker/linker_grpc_web_pb'
import { useAuthorizedGrpcClient } from 'src/adapter/grpcClient'
import {
  useReceptor,
  useHasReceptor,
} from 'src/pages/Receptors/receptors.hooks'
import { ModalFormId } from 'src/components/ModalForms'
import { useHideModal } from 'src/Modal/ModalStateContext'
import { useUser } from 'src/components/async/Users'

import { Source, LINKER_MODEL_IDS } from '../../Linkers.constants'
import type {
  ConfluenceNode,
  ConfluenceTree,
} from './ConfluenceFilePickerModal.types'

interface OnSubmitProps {
  caption: string
  node: ConfluenceNode
  /** Optional batch selection. If provided, `node` is ignored. */
  nodes?: ConfluenceNode[]
  isViaRequest?: boolean
  relevantDate?: Timestamp
}

export interface ConfluenceFilePickerContextValue {
  tree: ConfluenceTree | null
  setTree: React.Dispatch<React.SetStateAction<ConfluenceTree | null>>
  loading: boolean
  setLoading: (value: boolean) => void
  selectedNode: ConfluenceNode | null
  setSelectedNode: React.Dispatch<React.SetStateAction<ConfluenceNode | null>>
  /**
   * Files selected from Confluence, surfaced in the AddDocumentForm summary
   * (mirrors the SharePoint / Google Drive pattern).
   */
  filesSelected: ConfluenceNode[]
  setFilesSelected: React.Dispatch<React.SetStateAction<ConfluenceNode[]>>
  onSubmit: (props: OnSubmitProps) => Promise<void>
  source: Source
  setSource: (source: Source) => void
  subjectIds: string[]
  setSubjectIds: (ids: string[]) => void
}

const ConfluenceFilePickerContext = createContext<
  ConfluenceFilePickerContextValue | undefined
>(undefined)

export const ConfluenceFilePickerProvider: React.FC<{
  children: React.ReactNode
}> = ({ children }) => {
  const [tree, setTree] = useState<ConfluenceTree | null>(null)
  const [loadingState, setLoadingState] = useState<boolean>(true)
  const [selectedNode, setSelectedNode] = useState<ConfluenceNode | null>(null)
  const [filesSelected, setFilesSelected] = useState<ConfluenceNode[]>([])

  const [source, setSource] = useState<Source>('Evidence')
  const [subjectIds, setSubjectIds] = useState<string[]>([])

  const setLoading = (value: boolean): void => {
    setLoadingState(value)
  }

  const { data: HasReceptor } = useHasReceptor(
    LINKER_MODEL_IDS.CONFLUENCE,
    true,
  )
  const hasConfluenceReceptor = useMemo(
    () => HasReceptor?.getHasReceptor() || false,
    [HasReceptor],
  )

  const { data: receptor } = useReceptor({
    modelId: LINKER_MODEL_IDS.CONFLUENCE,
    shouldFetch: hasConfluenceReceptor,
  })

  const { data: user } = useUser()
  const userId = useMemo(() => user?.getId() || '', [user])

  const linkerClient = useAuthorizedGrpcClient(LinkerPromiseClient)

  const hide = useHideModal({
    modalId: ModalFormId.CONFLUENCE_FILE_PICKER,
  })

  const onSubmit = async ({
    caption,
    node,
    nodes,
    relevantDate,
    isViaRequest = false,
  }: OnSubmitProps): Promise<void> => {
    if (!receptor) {
      return
    }

    const receptorId = receptor.getId() || ''
    const request = new SetLinkRequest().setReceptorId(receptorId)

    const selectedNodes =
      Array.isArray(nodes) && nodes.length > 0 ? nodes : [node]

    const docs = selectedNodes.map((n) => {
      const nodeUrl = n.url ?? ''
      const nodeFileName = n.fileName || n.title || String(n.id)

      let ts: Timestamp | undefined
      const rawDate = (relevantDate ?? n.relevantDate) as string | undefined

      if (rawDate) {
        const parsed = new Date(rawDate)
        if (!Number.isNaN(parsed.getTime())) {
          ts = Timestamp.fromDate(parsed)
        }
      }

      return new LinkDoc()
        .setImportKey(`${n.spaceId}:${n.id}:${source}`)
        .setImportLink(nodeUrl)
        .setSubjectIdsList(subjectIds)
        .setDocumentType(
          source === 'Evidence'
            ? DOCUMENT_TYPE.EVIDENCE
            : DOCUMENT_TYPE.POLICYDOC,
        )
        .setRelevantDate(ts)
        .setIsViaRequest(isViaRequest)
        .setCaption(
          caption && caption.length > 0
            ? caption
            : n.title ?? String(n.fileName),
        )
        .setFileName(nodeFileName)
        .setReceptorModelId(receptor.getModelid())
        .setUserId(userId)
    })

    request.setDocumentsList(docs)

    hide()

    await linkerClient.setLink(request)

    setFilesSelected(selectedNodes)
  }

  return (
    <ConfluenceFilePickerContext.Provider
      value={{
        tree,
        setTree,
        loading: loadingState,
        setLoading,
        selectedNode,
        setSelectedNode,
        filesSelected,
        setFilesSelected,
        onSubmit,
        source,
        setSource,
        subjectIds,
        setSubjectIds,
      }}
    >
      {children}
    </ConfluenceFilePickerContext.Provider>
  )
}

export function useConfluenceFilePickerContext(): ConfluenceFilePickerContextValue {
  const ctx = useContext(ConfluenceFilePickerContext)
  if (!ctx) {
    throw new Error(
      'useConfluenceFilePickerContext must be used within ConfluenceFilePickerProvider',
    )
  }
  return ctx
}
